<template>
  <div>
    <document-preview v-model:document="currentPreview" />
    <q-list v-if="documentsCopy.length" bordered separator class="rounded-borders">
      <q-item v-if="currentFolder"
              clickable
              @click="openFolder(null)"
              active>
        <q-tooltip anchor="top middle" self="bottom middle">
          Terug naar het overzicht
        </q-tooltip>
        <q-item-section avatar>
          <q-icon
              name="folder_open"
              color="neutral-60"
              size="sm"
              aria-hidden="true"
          />
        </q-item-section>
        <q-item-section>
          {{ currentFolder.name }}
        </q-item-section>
      </q-item>
      <q-item v-else
              v-for="(folder, index) in folders"
              :key="`folder-${index}`"
              clickable
              @click="openFolder(folder)">
        <q-tooltip anchor="top middle" self="bottom middle">
          Open de map {{ folder.name }}
        </q-tooltip>
        <q-item-section avatar>
          <q-icon
              name="folder"
              color="neutral-60"
              size="sm"
              aria-hidden="true"
          />
        </q-item-section>
        <q-item-section avatar>
          <count-chip :count="folder.documents" />
        </q-item-section>
        <q-item-section>
          <q-item-label lines="1">
            {{ folder.name }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <div class="row" v-for="document in currentContent" :key="document.id">
        <div class="col">
          <q-item class="full-height"
                  clickable
                  @click="preview(document)"
                  :active="document.id === currentPreview?.id">
            <q-tooltip anchor="top middle" self="bottom middle">
              Bekijk {{ document.name }}
            </q-tooltip>
            <q-item-section v-if="currentFolder" avatar>
              <q-icon
                  name="subdirectory_arrow_right"
                  color="neutral-60"
                  size="sm"
                  aria-hidden="true"
              />
            </q-item-section>
            <q-item-section avatar>
              <q-icon
                  :name="documentTypes[document.type].icon || '-' "
                  color="neutral-60"
                  size="sm"
                  aria-hidden="true"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label lines="1"
                            clickable
                            @click="preview(document)">
                {{ document.name }}
                <q-item-label caption>
                  {{ $formatDateRelative(document.created_at) }}
                </q-item-label>
                <q-item-label caption>
                  {{ documentTypes[document.type].name || 'insert_drive_file' }}
                </q-item-label>
              </q-item-label>
            </q-item-section>
          </q-item>
        </div>
        <div class="col">
          <q-item>
            <q-item-section>
              <q-select v-if="canEdit"
                        label="Map"
                        style="width:180px"
                        filled
                        :options="folders.map(folder => folder.name)"
                        :model-value="document.meta_data.folder"
                        clearable
                        use-input
                        new-value-mode="add-unique"
                        @update:model-value="moveToFolder(document, $event)"
              >
                <q-tooltip anchor="top middle" self="bottom middle">
                  Selecteer een map of typ de naam van een nieuwe map om het document te verplaatsen
                </q-tooltip>
              </q-select>
            </q-item-section>
            <q-item-section side>
              <div class="q-gutter-sm">
                <icon-button
                    icon="save_alt"
                    color="neutral-60"
                    aria-label="Download document"
                    size="sm"
                    download
                    round
                    :href="$route('asset_management.download_asset', [document.id, `${document.name}.${document.extension}`])"
                />
                <icon-button
                    v-if="canEdit"
                    @click="deleteDocument(document)"
                    icon="clear"
                    color="negative"
                    aria-label="Verwijder document"
                    size="sm"
                    round
                />
              </div>
            </q-item-section>
          </q-item>
        </div>
      </div>
    </q-list>
    <document-upload v-if="canEdit"
                     v-model:documents="documentsCopy"
                     :uploadUrl="uploadUrl"
                     class="q-mt-md"
                     @documentUploaded="newDocumentUploaded" />
  </div>
</template>

<script>
import { documentTypes } from '~HIOR/constants'
import CountChip from '../badges_chips/CountChip'

export default {
  components: { CountChip },
  props: {
    documents: {
      type: Array,
      default: () => [],
    },
    uploadUrl: {
      type: String,
    },
    deleteUrl: {
      type: String,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allFolders () {
      return this.documentsCopy.map((document) => document.meta_data.folder)
    },
    folders () {
      return this.allFolders.reduce((folders, folderName) => {
        if (!folderName) return folders

        let folder = folders.find(folder => folder.name === folderName)
        if (folder) folder.documents++
        else {
          folders.push({
            name: folderName,
            documents: 1,
          })
        }
        return folders
      }, [])
    },
    currentContent () {
      return this.documentsCopy.filter(document => {
        if (!this.currentFolder) return !document.meta_data.folder
        return this.currentFolder.name === document.meta_data.folder
      })
    },
  },
  data () {
    return {
      currentFolder: null,
      currentPreview: null,
      documentsCopy: [
        ...this.documents,
      ],
      documentTypes,
    }
  },
  methods: {
    newDocumentUploaded (document) {
      this.preview(document)
      if (!this.currentFolder) return
      this.moveToFolder(document, this.currentFolder.name)
      this.unpreview(document)
    },
    async moveToFolder (document, folderName) {
      let originalFolder = document.meta_data.folder
      document.meta_data.folder = folderName || ''

      try {
        await axios.post(this.$route('asset_management.asset_meta'), {
          key: 'folder',
          asset_id: document.id,
          value: folderName,
        })
        this.unpreview(document)
      } catch (error) {
        console.error(error)
        document.meta_data.folder = originalFolder
        this.$toast('error', `Verplaatsen naar ${folderName} mislukt`)
      }
      this.$forceUpdate()
    },
    async deleteDocument (document) {
      if (await this.$confirm({
        title: 'Verwijderen',
        messages: [`Weet u zeker dat u "${document.name}" wil verwijderen?`],
      }))
        try {
          await axios.delete(this.deleteUrl, {
            data: { asset_id: document.id },
          })
          this.documentsCopy = this.documentsCopy.filter(item => item.id !== document.id)
          this.unpreview(document)
        } catch (error) {
          console.error(error)
          await this.$toast('error', 'Bestand kan niet verwijderd worden')
        }
    },
    openFolder (folder) {
      this.currentFolder = folder
      this.unpreview()
    },
    preview (document) {
      this.currentPreview = document
    },
    unpreview (document = null) {
      if (document && this.currentPreview?.id !== document.id) return
      this.currentPreview = null
    },
  },
}
</script>
